package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.project.dashboard.PopupEngineCard
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.settings.*
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.Event
import org.w3c.dom.url.URL
import kotlin.js.Promise

internal fun fillLinkedInMessage(template: String, lead: All): String {
    val name = listOf(lead.vorname.trim(), lead.nachname.trim()).filter { it.isNotBlank() }
        .joinToString(" ").ifBlank { lead.ansprechpartner.trim() }
    var text = template.replace(Regex("%ansprechpartner%", RegexOption.IGNORE_CASE)) { name }
    mapOf("Vorname" to lead.vorname, "Nachname" to lead.nachname, "Firma" to lead.firma).forEach { (key, value) ->
        text = text.replace(Regex("\\{\\{?${key}\\}?\\}", RegexOption.IGNORE_CASE)) { value }
    }
    return text
}

internal fun linkedInDispatchText(entries: List<All>, types: List<VertriebsindexApi.LeadTypEintrag>,
    lead: All, userLeadType: String, action: String): String {
    val type = lead.leadTyp.ifBlank { userLeadType }
    val typeId = types.firstOrNull { it.leadTyp == type || it.id == type }?.id
    val kind = when (action) {
        "DM_SENT" -> "DM-Nachricht"
        "MATERIAL_SENT" -> "Materialbestätigung"
        else -> "Follow-up"
    }
    val template = entries.firstOrNull {
        it.linkedinDmLeadTypId == typeId && it.linkedinDmTyp == kind && it.linkedinDmInhalt.isNotBlank()
    } ?: entries.firstOrNull {
        it.linkedinDmLeadTypId == "__all__" && it.linkedinDmTyp == kind && it.linkedinDmInhalt.isNotBlank()
    } ?: return ""
    return fillLinkedInMessage(template.linkedinDmInhalt, lead)
}

internal fun linkedInDispatchProfileUrl(value: String): String {
    val url = runCatching { URL(value) }.getOrNull() ?: return ""
    return if ((url.hostname == "linkedin.com" || url.hostname.endsWith(".linkedin.com")) &&
        url.protocol == "https:" && url.pathname.startsWith("/in/") && url.pathname.removePrefix("/in/").trim('/').isNotBlank()) url.href else ""
}

/** Copy in the original click gesture, then open the person's profile, never the messaging composer. */
internal fun copyLinkedInTextAndOpenProfile(text: String, profile: String): Promise<Unit> {
    val url = linkedInDispatchProfileUrl(profile)
    check(url.isNotBlank()) { "Kein gültiges LinkedIn-Profil hinterlegt." }
    return copyDispatchTextAndOpen(text, url)
}

private fun copyDispatchTextFallback(text: String) {
    val previousFocus = document.activeElement as? HTMLElement
    val field = document.createElement("textarea") as HTMLTextAreaElement
    var payloadWritten = false
    val onCopy: (Event) -> Unit = { event ->
        val data = event.asDynamic().clipboardData
        if (data != null) {
            data.setData("text/plain", text)
            event.preventDefault()
            payloadWritten = true
        }
    }
    try {
        field.value = text
        field.setAttribute("readonly", "")
        field.style.position = "fixed"
        field.style.left = "0"
        field.style.top = "0"
        field.style.opacity = "0"
        document.body?.appendChild(field)
        document.addEventListener("copy", onCopy)
        field.focus()
        field.select()
        field.setSelectionRange(0, text.length)
        val copied = document.asDynamic().execCommand("copy") as? Boolean ?: false
        check(copied && payloadWritten) { "Nachricht konnte nicht kopiert werden. Bitte die Zwischenablage im Browser erlauben." }
    } finally {
        document.removeEventListener("copy", onCopy)
        field.parentNode?.removeChild(field)
        previousFocus?.focus()
    }
}

private fun copyDispatchText(text: String): Promise<Unit> {
    check(text.isNotBlank()) { "Keine passende Vorlage im DM Script hinterlegt." }
    val clipboard = window.navigator.asDynamic().clipboard
    try {
        if (clipboard == null) {
            copyDispatchTextFallback(text)
            return Promise.resolve(Unit)
        }
        val pending: Promise<dynamic> = clipboard.writeText(text)
        return pending.then<Unit> { _: dynamic -> Unit }.catch {
            copyDispatchTextFallback(text)
            Unit
        }
    } catch (_: Exception) {
        copyDispatchTextFallback(text)
        return Promise.resolve(Unit)
    }
}

private fun copyDispatchTextAndOpen(text: String, url: String): Promise<Unit> {
    return copyDispatchText(text).then {
        val popup = window.open(url, "_blank")
        popup?.opener = null
        check(popup != null) { "Text kopiert. Bitte das Öffnen des Versandkanals im Browser erlauben." }
        Unit
    }
}

private fun materialMobile(lead: All): String = lead.mobil.trim().replace(Regex("[\\s()/-]"), "").let {
        when { it.startsWith("+") -> it.drop(1); it.startsWith("00") -> it.drop(2); it.startsWith("0") -> "49" + it.drop(1); else -> it }
    }.takeIf { it.matches(Regex("[1-9][0-9]{6,14}")) }.orEmpty()

private fun materialEmailAvailable(lead: All) = lead.email.trim().matches(Regex("[^\\s@]+@[^\\s@]+\\.[^\\s@]+"))

internal fun sendMaterialText(text: String, lead: All, channel: String): Promise<Unit> {
    check(text.isNotBlank()) { "Bitte zuerst eine Materialbestätigung für diesen Lead-Typ im DM Script hinterlegen." }
    fun encode(value: String): String = js("encodeURIComponent")(value) as String
    return when (channel) {
        "email" -> {
            check(materialEmailAvailable(lead)) { "Keine gültige E-Mail-Adresse hinterlegt." }
            copyDispatchText(text).then {
                window.location.href = "mailto:${encode(lead.email.trim())}?subject=${encode("Material")}&body=${encode(text)}"
                Unit
            }
        }
        "whatsapp" -> {
            val mobile = materialMobile(lead)
            check(mobile.isNotBlank()) { "Keine gültige Mobilnummer hinterlegt." }
            copyDispatchTextAndOpen(text, "https://wa.me/$mobile?text=${encode(text)}")
        }
        "linkedin" -> {
            check(lead.linkedinNetzwerkstatus != "Nicht vernetzt") { "Kontakt ist nicht vernetzt." }
            copyLinkedInTextAndOpenProfile(text, lead.linkedin)
        }
        else -> error("Unbekannter Versandkanal.")
    }
}

@Composable
internal fun LinkedInDispatchPopup(lead: All, text: String, error: String, busy: Boolean,
    selectedChannel: String?, ready: Boolean, onConfirm: () -> Unit,
    onClose: () -> Unit, onSelect: (String) -> Unit, action: String = "MATERIAL_SENT") {
    val scope = rememberCoroutineScope()
    var hovered by remember { mutableStateOf<String?>(null) }
    var clicked by remember { mutableStateOf<String?>(null) }
    val execution = action in setOf("CALL_COMPLETED", "CALL_DONE", "APPOINTMENT_DONE")
    val callExecution = action in setOf("CALL_COMPLETED", "CALL_DONE")
    val activity = if (callExecution) "Call" else "Termin"
    val contactName = fillLinkedInMessage("%ansprechpartner%", lead)
    val confirmationVisible = execution || selectedChannel != null
    PopupEngineCard(windowKey = "linkedin-material-channel", zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 620, heightPx = 440, minWidthPx = 380, minHeightPx = 320,
        centered = true, viewportAnchored = true, resizable = true, onClose = { if (!busy) onClose() }) {
        Div({ style { height(100.percent); minHeight(0.px); boxSizing("border-box"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); textAlign("center") } }) {
            Div({ style { flex("1 1 0"); minHeight(0.px); padding(24.px); boxSizing("border-box"); overflowY("auto"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(32.px) } }) {
            H3({ style { margin(0.px) } }) { Text(when {
                execution -> "Wurde der $activity${if (contactName.isNotBlank()) " mit $contactName" else ""} durchgeführt?"
                action == "MATERIAL_SENT" -> "Wie soll das Material versendet werden?"
                else -> "Dein LinkedIn-Kontakt wurde geöffnet"
            }) }
            if (text.isBlank() && action == "MATERIAL_SENT") Div { Text("Bitte erst Materialtext anlegen") }
            if (!execution && action != "MATERIAL_SENT") {
                Img(src = "images/linkedin.svg", attrs = {
                    attr("alt", "LinkedIn"); style { width(36.px); height(36.px); property("filter", UISettings.STATE_BUTTON_ICON_FILTER) }
                })
            } else if (!execution) {
            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); gap(12.px) } }) {
                val channels = buildList {
                    if (materialEmailAvailable(lead)) add("email" to "E-Mail")
                    if (materialMobile(lead).isNotBlank()) add("whatsapp" to "WhatsApp")
                    if (lead.linkedinNetzwerkstatus != "Nicht vernetzt" && linkedInDispatchProfileUrl(lead.linkedin).isNotBlank()) add("linkedin" to "LinkedIn")
                }
                channels.forEach { (key, label) ->
                    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(8.px); minWidth(88.px) } }) {
                    Button(attrs = {
                        attr("type", "button"); attr("aria-label", label); attr("title", label)
                        if (busy || text.isBlank()) attr("disabled", "")
                        style { border(0.px); backgroundColor(Color.transparent); padding(8.px); cursor("pointer"); display(DisplayStyle.Flex); alignItems(AlignItems.Center) }
                        onMouseEnter { hovered = key }
                        onMouseLeave { if (hovered == key) hovered = null }
                        onClick {
                            if (!busy && text.isNotBlank()) {
                                clicked = key
                                scope.launch { delay(300); if (clicked == key) clicked = null }
                                onSelect(key)
                            }
                        }
                    }) {
                        Img(src = "images/${if (key == "email") "mail" else key}.svg", attrs = {
                            attr("alt", ""); style {
                                width(36.px); height(36.px)
                                property("filter", if (clicked == key) UISettings.ICON_FILTER_ACCENT else UISettings.STATE_BUTTON_ICON_FILTER)
                                property("transform", if (hovered == key && !busy && text.isNotBlank()) "scale(1.12)" else "scale(1)")
                                property("transition", "transform 150ms ease, filter 120ms ease")
                            }
                        })
                    }
                    Div({ style { fontSize(12.px); textAlign("center") } }) { Text(label) }
                    }
                }
            }
            }
            }
            Div({ style { flex("1 1 0"); minHeight(0.px); padding(24.px); boxSizing("border-box"); overflowY("auto"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(32.px) } }) {
                Div({
                    attr("aria-hidden", (!confirmationVisible).toString())
                    style {
                        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(32.px)
                        opacity(if (confirmationVisible) 1.0 else 0.0)
                        property("transition", "opacity 500ms ease")
                        property("pointer-events", if (confirmationVisible) "auto" else "none")
                    }
                }) {
                    if (!execution) H3({ style { margin(0.px) } }) { Text(when (action) {
                        "DM_SENT" -> "Bitte bestätige die DM"
                        "FOLLOWUP" -> "Bitte bestätige das Follow-up"
                        else -> "Bitte bestätige den Versand"
                    }) }
                    LumaticaButton(if (execution) "$activity bestätigen" else "Bestätigen", onConfirm,
                        enabled = ready && confirmationVisible && !busy, variant = LumaticaButtonVariant.Action)
                    if (error.isNotBlank()) P({ attr("role", "alert") }) { Text(error) }
                }
            }
        }
    }
}

@Composable
internal fun LinkedInDispatchPreview(action: String, text: String) {
    Div({ style { padding(32.px); flexShrink(0); boxSizing("border-box"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(32.px); textAlign("center") } }) {
        Div({ style { fontSize(20.px); fontWeight("600"); lineHeight("1.4") } }) {
            Text(when (action) {
                "DM_SENT" -> "Folgender Text wird zur Ansprache genutzt:"
                "MATERIAL_SENT" -> "Folgender Text wird zur Materialbestätigung genutzt:"
                else -> "Folgender Text wird zum Follow-up genutzt:"
            })
        }
        if (text.isNotBlank()) Div({ style {
            width(100.percent); maxWidth(720.px); boxSizing("border-box"); padding(16.px, 20.px)
            borderRadius(12.px); backgroundColor(Color("rgba(255,255,255,0.12)"))
            property("border", "1px solid rgba(255,255,255,0.14)")
            whiteSpace("pre-wrap"); property("overflow-wrap", "anywhere")
            fontSize(14.px); lineHeight("1.6"); textAlign("left")
        } }) { Text(text) }
        else P({ style { fontSize(12.px); opacity(.65); textAlign("center") } }) { Text("Für diesen Lead-Typ ist noch kein Text im DM Script hinterlegt.") }
    }
}
